(function () {
  'use strict';
  if (window.SpeedyDroneVideo) return;

  const players = new Map();
  const scriptData = document.currentScript ? document.currentScript.dataset : {};
  const labels = {
    play: scriptData.playLabel || 'Play video',
    pause: scriptData.pauseLabel || 'Pause video',
    retry: scriptData.retryLabel || 'Retry video',
    unavailable: scriptData.unavailableLabel || 'Video is unavailable.',
    watch: scriptData.watchLabel || 'Open video'
  };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);
  let sequence = 0;
  let suspended = false;
  let frame = 0;

  function visible(element) {
    if (!element.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"], [data-sd-video-active="false"]')) return false;
    const rect = element.getBoundingClientRect();
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;
    const x = Math.max(0, Math.min(rect.right, width) - Math.max(rect.left, 0));
    const y = Math.max(0, Math.min(rect.bottom, height) - Math.max(rect.top, 0));
    return rect.width > 0 && rect.height > 0 &&
      (x * y) / (Math.min(rect.width, width) * Math.min(rect.height, height)) >= 0.15;
  }

  // The pill sits on the box that frames the video. Heroes and video cards can stack their text
  // under the video on mobile, so .m-hero and .m-image-card__inner may be taller than the video;
  // the hero's media wrapper and the card's player box are not.
  function controlHost(element) {
    const link = element.closest('a');
    return element.closest('.m-hero__bg, .m-image-card__inner > m-video-component') ||
      element.closest('.m-hero') || element.closest('.m-product-card__media') ||
      (link ? link.parentElement : element.parentElement);
  }

  function makeButton(element, host) {
    if (!element.id) element.id = 'sd-controlled-video-' + (++sequence);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sd-video-toggle';
    button.setAttribute('aria-controls', element.id);
    button.textContent = labels.play;
    button.setAttribute('aria-label', labels.play);
    if (getComputedStyle(host).position === 'static') host.classList.add('sd-video-controls-host');
    host.appendChild(button);
    return button;
  }

  function register(video, options) {
    if (!video || players.has(video)) return players.get(video);
    options = options || {};
    const abort = new AbortController();
    const eventOptions = { signal: abort.signal };
    const keepControls = options.keepControls === true || video.dataset.sdVideoControls === 'native';
    const autoPlay = options.autoPlay !== undefined ? options.autoPlay : video.dataset.sdVideoAutoplay === 'true';
    const host = options.host || controlHost(video);
    const existingButton = options.button || null;
    const button = keepControls ? null : existingButton || makeButton(video, host);
    let intent = null;
    let loaded = false;
    let pending = false;
    let failed = false;
    let disposed = false;
    let expectedPlay = false;
    let expectedPause = false;

    video.removeAttribute('autoplay');
    video.autoplay = false;
    video.controls = keepControls;
    video.playsInline = true;
    video.removeAttribute('aria-hidden');
    if (video.getAttribute('tabindex') === '-1') video.removeAttribute('tabindex');
    video.dataset.sdVideoManaged = 'true';
    if (!keepControls) {
      video.muted = true;
      video.defaultMuted = true;
    }

    function paint() {
      if (!button) return;
      const text = failed ? labels.retry : video.paused ? labels.play : labels.pause;
      const label = button.querySelector('[data-sd-video-label]');
      if ((label || button).textContent !== text) (label || button).textContent = text;
      if (button.getAttribute('aria-label') !== text) button.setAttribute('aria-label', text);
      button.removeAttribute('aria-pressed');
      button.hidden = false;
    }

    function load() {
      if (loaded && !failed) return;
      if (video.dataset.src) {
        video.src = video.dataset.src;
        delete video.dataset.src;
      }
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        delete source.dataset.src;
      });
      loaded = true;
      video.preload = 'metadata';
      video.load();
    }

    function allowed() {
      return !disposed && !document.hidden && !suspended && visible(video) && intent !== false &&
        (intent === true || (autoPlay && !reducedMotion.matches && !saveData()));
    }

    function pause() {
      if (!video.paused) {
        expectedPause = true;
        video.pause();
      }
      paint();
    }

    function sync() {
      if (!allowed()) { pause(); return; }
      if (failed || pending || !video.paused) { paint(); return; }
      load();
      pending = true;
      expectedPlay = true;
      let request;
      try { request = video.play(); } catch (error) { request = Promise.reject(error); }
      Promise.resolve(request).then(() => {
        if (!allowed()) pause();
      }).catch(() => {
        expectedPlay = false;
        if (allowed()) failed = true;
      }).finally(() => {
        pending = false;
        if (!disposed) paint();
      });
    }

    if (button) button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      intent = video.paused || failed;
      if (failed) { loaded = false; failed = false; }
      sync();
    }, eventOptions);

    video.addEventListener('play', () => {
      if (expectedPlay) expectedPlay = false;
      else if (keepControls) intent = true;
      if (!allowed()) pause();
      paint();
    }, eventOptions);
    video.addEventListener('playing', () => {
      if (!allowed()) pause();
      paint();
    }, eventOptions);
    video.addEventListener('pause', () => {
      if (expectedPause) expectedPause = false;
      else if (keepControls && !video.ended) intent = false;
      paint();
    }, eventOptions);
    video.addEventListener('error', () => { failed = true; paint(); }, eventOptions);
    video.addEventListener('loadeddata', () => {
      if (options.readyClass) video.classList.add(options.readyClass);
      if (video.dataset.sdVideoReadyParent) video.parentElement.classList.add(video.dataset.sdVideoReadyParent);
    }, eventOptions);

    const controller = {
      sync,
      requestPlay() { intent = true; if (failed) { loaded = false; failed = false; } sync(); },
      requestPause() { intent = false; sync(); },
      preload() { if (!reducedMotion.matches && !saveData()) load(); },
      preferenceChanged() { if (reducedMotion.matches && intent !== false) intent = null; sync(); },
      destroy() {
        disposed = true;
        abort.abort();
        video.pause();
        if (preloadObserver) preloadObserver.unobserve(video);
        if (playbackObserver) playbackObserver.unobserve(video);
        if (button && !existingButton) button.remove();
        video.controls = true;
        delete video.dataset.sdVideoManaged;
        players.delete(video);
      }
    };
    players.set(video, controller);
    if (preloadObserver) preloadObserver.observe(video);
    if (playbackObserver) playbackObserver.observe(video);
    paint();
    sync();
    return controller;
  }

  const preloadObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting && players.has(entry.target)) {
        players.get(entry.target).preload();
        preloadObserver.unobserve(entry.target);
      }
    });
  }, { rootMargin: '280px 0px', threshold: 0 }) : null;

  const playbackObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (players.has(entry.target)) players.get(entry.target).sync(); });
  }, { rootMargin: '0px', threshold: [0, 0.15, 0.5, 1] }) : null;

  function init(scope) {
    if (scope.matches && scope.matches('video[data-sd-video]')) register(scope);
    scope.querySelectorAll('video[data-sd-video]').forEach(video => register(video));
  }

  function refresh(scope) {
    players.forEach((controller, video) => {
      if (!video.isConnected) controller.destroy();
      else if (!scope || scope === video || scope.contains(video)) controller.sync();
    });
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = 0; refresh(); });
  }

  window.SpeedyDroneVideo = { register, init, refresh, visible, makeButton, controlHost, labels };
  document.addEventListener('visibilitychange', () => refresh());
  document.addEventListener('sd:video:refresh', event => refresh(event.target));
  window.addEventListener('pagehide', () => { suspended = true; refresh(); });
  window.addEventListener('pageshow', () => { suspended = false; refresh(); });
  window.addEventListener('resize', schedule, { passive: true });
  // A shared listener also covers very large videos whose intersection ratio never reaches a threshold.
  window.addEventListener('scroll', schedule, { passive: true });
  const onMotion = () => players.forEach(controller => controller.preferenceChanged());
  if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotion);
  else reducedMotion.addListener(onMotion);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    players.forEach((controller, video) => { if (event.target.contains(video)) controller.destroy(); });
  });

  function start() {
    init(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(node => {
        if (node.nodeType === 1) init(node);
      }));
      players.forEach((controller, video) => { if (!video.isConnected) controller.destroy(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
